// Single source of truth for project data.
// Consumed by Portfolio.js (grid + filtering) and ProjectPage.js (detail view).
//
// Shape:
//   id              - unique numeric id (matches /project/:id route)
//   image           - imported image asset
//   title           - display title
//   shortDescription- one-line hook used on the portfolio card AND as the detail page subtitle
//   tags            - array of lowercase tags used by the portfolio filter (e.g. "vr", "ai")
//   category        - display-friendly category label shown on the project detail page
//   fullDescription - long-form description (paragraphs separated by blank lines)
//   technologies    - array of tech labels rendered as pills
//   duration        - project duration string
//   role            - your role on the project
//   challenges      - array of challenge bullet strings
//   features        - array of feature bullet strings
//   githubLink      - optional GitHub URL
//   liveDemoLink    - optional demo URL (YouTube, Google Drive, LinkedIn embed all supported)
//   metaStoreLink   - optional Meta Store URL
//   team            - optional [{ name, linkedinUrl }] for team-built projects

import project1 from '../assets/images/project1.png';
import project2 from '../assets/images/project2.png';
import project3 from '../assets/images/project3.png';
import traffic from '../assets/images/Untitled design(1).png';
import vrCricket from '../assets/images/vr-cricket.png';
import gorillaVs100Men from '../assets/images/GorillaVs100Men.jpg';
import swara from '../assets/images/Swara.png';
import bookworm from '../assets/images/BookWorm.png';
import chotu from '../assets/images/Chotu.png';
import giantPianoTiles from '../assets/images/GiantPianoTiles.png';
import mutantGuns from '../assets/images/MutantGuns.png';

export const projects = [
    {
        id: 1,
        image: gorillaVs100Men,
        title: "Gorilla vs 100 Men",
        shortDescription: "Wave-Based VR Survival Combat — 80,000+ installs on Meta Store",
        blockColor: "cobalt",
        motif: "xr",
        tags: ["vr"],
        category: "VR Game Development",
        fullDescription: `Gorilla vs 100 Men draws inspiration from the viral "100 men vs 1 gorilla" meme while incorporating beloved locomotion mechanics similar to Gorilla Tag and visceral combat systems popularized by games like Blade & Sorcery.

Players embody a powerful gorilla, using authentic primate arm-swinging locomotion to navigate dynamic environments while strategically avoiding encirclement and unleashing devastating attacks on challengers. The game evolved from a tech demo into a complete wave-based survival experience with intelligent enemy AI and structured gameplay progression.

Behind the intense action lies an unexpected narrative that honors Harambe's legacy while exploring deeper themes of wildlife conservation, humanity's complex relationship with the natural world, and the implications of our rapidly advancing technological landscape—including AI and robotics. This isn't just another action game; it's an interactive experience that uses thrilling gameplay to spark meaningful conversations about our responsibilities as stewards of the planet.

The MVP focuses on creating a polished, replayable experience with wave-based enemy spawning, strategic outdoor battleground environments, and immersive audio-visual presentation that transforms the foundation into a market-ready VR experience.

Co-developed with a five-person international team, Gorilla vs 100 Men has surpassed 80,000+ installs on the Meta Store.`,
        technologies: ["Unity", "C#", "GorillaTag-Like Locomotion", "Unity NavMesh", "Puppet Master", "Final IK", "Meta Quest"],
        duration: "2 weeks",
        role: "VR Developer & Team Collaborator",
        challenges: [
            "Implementing intelligent enemy AI with wave-based spawning system",
            "Creating authentic gorilla locomotion using arm-swinging mechanics",
            "Integrating advanced ragdoll physics with combat mechanics",
        ],
        features: [
            "Authentic primate arm-swinging locomotion mechanics",
            "Wave-based survival system with intelligent enemy AI",
            "Strategic outdoor battleground with environmental interactions",
            "Advanced ragdoll physics and inverse kinematics",
            "Round-based progression with victory/defeat conditions",
            "Enemy Grab and Throw mechanics",
            "Dynamic audio system with combat sounds",
        ],
        githubLink: "https://github.com/amirmohideen/GorillageddonXRB/",
        liveDemoLink: "https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:7350106421683793920?compact=1",
        metaStoreLink: "https://www.meta.com/experiences/24276974678594511/",
        team: [
            { name: "Amir Mohideen", linkedinUrl: "https://www.linkedin.com/in/amir99/" },
            { name: "Micheal Deering", linkedinUrl: "https://www.linkedin.com/in/mdeering/" },
            { name: "Julian Houston", linkedinUrl: "https://www.linkedin.com/in/julianhouston/" },
            { name: "Javed Akhter", linkedinUrl: "https://www.linkedin.com/in/javed-akhter-shaik-76836320b/" },
            { name: "Vijay Kagupati (Me)", linkedinUrl: "https://www.linkedin.com/in/vijaykagupati/" },
        ],
    },
    {
        id: 2,
        image: swara,
        title: "Swara",
        shortDescription: "VR Music Studio · Mic capture, drums, recording · 1 week",
        blockColor: "sun",
        motif: "hud",
        tags: ["vr"],
        category: "VR Music",
        fullDescription: `Swara is an immersive VR music recording studio that transforms virtual reality into a musical playground. Imagine stepping into a virtual concert hall with gently lit stage, and an audience.

The prototype focuses on two core pillars: VR Concert Stage where you stand onstage with lyrics display and Karaoke. A Music Studio environment where live microphone input, and Acoustic Drum Pads and a session recording system.

Users can go to a virtual microphone, play the drums, and begin singing and start recording their performance, and replay or save that recording.

The experience includes a SessionRecorder System that logs microphone buffer and pad events, then mixes everything down into a single WAV file. The project demonstrates the potential of VR technology in collaborative music creation, showing how virtual environments can enhance creative expression and social interaction through shared musical experiences.`,
        technologies: ["Unity", "C#", "Meta XR All-in-One SDK", "Spatial Audio", "Real-time Audio Processing"],
        duration: "1 Week",
        role: "Solo Developer",
        challenges: [
            "Implementing realistic baked lighting in Unity for concert hall atmosphere",
            "Performance optimization when handling heavy 3D Assets",
        ],
        features: [
            "VR Concert Hall Environment with studio atmosphere",
            "Mic Capture & Spatial Audio with 3D positioning and hall reverb",
            "Acoustic Drum Pad with 3D audio",
            "SessionRecorder System for real-time collaborative audio mixing",
            "Real-time WAV file generation from recorded sessions",
        ],
        githubLink: "https://github.com/VijayKagupati/Swara",
        liveDemoLink: "https://drive.google.com/file/d/1zi9jpRklEkNBYSWR8tQp99AzE9SSv3b1/view?usp=drive_link",
    },
    {
        id: 3,
        image: bookworm,
        title: "BookWorm",
        shortDescription: "Duolingo meets Beat Saber · AI tutor + streak garden · 1 week",
        blockColor: "tomato",
        motif: "dialog",
        tags: ["vr", "ai"],
        category: "MR Education",
        fullDescription: `BookWorm is an innovative MR "micro-learning" playground that transforms any skill or topic into a daily habit through short, game-like interactions and positive reinforcement loops. Think of it as Duolingo meets Beat Saber: each lesson is a 2–3-minute MR mini-game teaching one concept, punctuated by a "Streak Garden" that visualizes your habit.

            It features an AI-powered voice tutor that guides you through lessons, providing personalized feedback and support. The tutor uses game state data to recognize your environment, making the experience feel more interactive and engaging.

The application features a single mini-game module where users "grab" or point at floating "concept tokens" and drop them into labeled bins, with immediate visual/audio feedback. The core experience is built around basic arithmetic and vocabulary flashcards, making learning engaging and interactive.

The "Streak Garden" visualization shows a garden that grows with each completed session, creating a visual representation of your learning journey

BookWorm demonstrates the potential of MR technology in gamified education, showing how traditional learning can be enhanced through immersive, habit-forming digital experiences with AI-powered tutoring support.`,
        technologies: ["Unity", "C#", "Meta XR SDK", "Quest 3", "Groq API", "AI/ML", "JSON Data Persistence"],
        duration: "1 Week",
        role: "Solo Developer",
        challenges: [
            "Implementing adaptive difficulty based on performance metrics",
            "Creating engaging gamified learning mechanics with concept tokens",
            "Developing data persistence system using JSON for streak tracking",
            "Integrating voice AI tutor with camera access for interactive guidance",
        ],
        features: [
            "Interactive concept token grab-and-drop mini-games",
            "Streak Garden visualization with growing plants",
            "Adaptive difficulty system based on user performance",
            "Voice AI tutor with game state data integration for personalized guidance",
            "JSON-based data persistence for progress tracking",
            "Immediate visual/audio feedback for learning reinforcement",
            "Basic arithmetic and vocabulary flashcard modules",
            "Habit-forming 2-3 minute learning sessions",
        ],
        githubLink: "https://github.com/VijayKagupati/Book-Worm",
        liveDemoLink: "https://drive.google.com/file/d/1F1yRUag4eXpno6mUUtPsBQEEVuA9W4F1/view?usp=drive_link",
    },
    {
        id: 4,
        image: vrCricket,
        title: "VR Cricket",
        shortDescription: "Stadium cricket sim with physics batting · 4s, 6s, wickets · Built in a week",
        blockColor: "cobalt",
        motif: "macwin",
        tags: ["vr"],
        category: "VR Development",
        fullDescription: `VR Cricket brings the thrill of cricket to virtual reality, placing you right in the middle of a packed stadium for the finals! Built in under one week during XR Bootcamp, this immersive experience demonstrates the potential of VR technology in sports simulation and gaming.

Feel the pressure of the finals as you stand at the crease, virtual bat in hand, with thousands of virtual spectators watching your every move. The project features a stadium scene with cricket pitch dimensions, realistic batting mechanics with physics-driven collisions, and a dynamic scoring system that automatically detects 4s, 6s, and wicket dismissals.`,
        technologies: ["Unity", "C#", "Meta XR SDK", "Quest 2", "Blender"],
        duration: "Under 1 Week",
        role: "VR Developer",
        challenges: [
            "Implementing realistic physics for ball-bat interaction system",
            "Creating stadium atmosphere with crowd reactions",
            "Optimizing performance for smooth VR experience on Quest hardware",
        ],
        features: [
            "Cricket stadium environment with professional pitch dimensions",
            "Physics-driven ball-bat collision system with trajectories",
            "Dynamic scoring system with auto-detection of 4s, 6s, and wickets",
            "Immersive audio with stadium ambiance and crowd reactions",
            "Natural VR batting mechanics with swing speed and angle detection",
            "Real-time scoreboard updates and match statistics",
        ],
        githubLink: "https://github.com/VijayKagupati/VR-Cricket",
        liveDemoLink: "https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:7334163662854606849?compact=1",
    },
    {
        id: 5,
        image: chotu,
        title: "Chotu",
        shortDescription: "On-device LLM bot · STT/TTS · Personality-driven · 1 week",
        blockColor: "sun",
        motif: "dialog",
        tags: ["vr", "ai"],
        category: "XR AI Development",
        fullDescription: `Chotu is a compact yet powerful AI assistant bot designed to handle everyday tasks and provide intelligent assistance across various domains. Built with on-device LLM model using LLMUnity, STT & TTS using Wit.AI.

The bot features conversational AI, task automation, and learning capabilities that improve over time through user interactions. Chotu can handle scheduling, information retrieval, basic calculations, and contextual conversations with personality and charm.`,
        technologies: ["Unity", "C#", "LLMUnity", "Wit.AI", "Meta XR SDK", "AI/ML"],
        duration: "1 Week",
        role: "Solo Developer",
        challenges: [
            "Creating engaging conversational personality",
            "Implementing Speech-to-Text and Text-to-Speech systems",
            "Developing On-device LLM model for efficient processing",
        ],
        features: [
            "Natural conversational interface",
            "On-device LLM model",
            "Speech to Text and Text to Speech integration",
            "Personality-driven responses",
        ],
        githubLink: "",
        liveDemoLink: "https://drive.google.com/file/d/1HuPXnDs9zYYJaC8ksM73xblhd9XeolEy/view",
    },
    {
        id: 6,
        image: giantPianoTiles,
        title: "Giant Piano Tiles",
        shortDescription: "Piano tiles on beat · 3 musical worlds · Beat-synced collision · 2 weeks",
        blockColor: "tomato",
        motif: "crt",
        tags: ["vr"],
        category: "VR Development",
        fullDescription: `Giant Piano Tiles transforms the classic mobile game into an immersive VR experience where players step into a virtual world of music and rhythm. This collaborative project was developed by a talented team of four developers, creating a physically engaging musical game that combines movement, timing, and melody.

Players use their body to step on giant piano tiles in virtual space, creating music through physical movement and precise timing. The game features beat-synchronized tile generation that creates piano tiles exactly on beats according to the rhythm of the music, ensuring perfect musical timing and flow.

The experience includes collision detection systems where piano tiles physically move down when stepped on or pressed, creating satisfying tactile feedback. The game offers VR mode with comprehensive safety features including adjustable speeds and scales, vignetting and FOV reduction, customizable settings for haptics, vignette effects, safety features, and volume controls. In immersive mode, the environment turns red when players approach safety boundaries.

The project features multiple difficulty levels with increasing speeds and decreasing tile sizes, accompanied by specialized development tools that streamlined the creation process. Players can explore different musical worlds with colored environments and themed visuals across three distinct initial worlds. The game prioritizes accessibility with improved UI and 2D visuals featuring enhanced text and object contrast, along with gameplay and visual adjustments to ensure usability for all players regardless of sensitivities or accessibility needs.`,
        technologies: ["Unity", "C#", "Meta Quest", "Meta XR SDK"],
        duration: "2 Weeks",
        role: "VR Developer",
        challenges: [
            "Implementing beat-synchronized tile generation with perfect musical timing",
            "Building specialized development tools to accelerate the creation process",
            "Creating aesthetically pleasing and immersive environments",
        ],
        features: [
            "Beat-synchronized piano tile generation matching musical rhythm",
            "Physical tile collision detection with realistic movement feedback",
            "Comprehensive accessibility options including remappable controls",
            "Multiple difficulty levels with adjustable speeds and tile sizes",
            "Three distinct musical worlds with themed environments",
            "Advanced safety systems with boundary warnings and customizable settings",
            "Improved UI design with enhanced contrast for accessibility",
            "Development tools for efficient content creation",
        ],
        githubLink: "",
        liveDemoLink: "https://drive.google.com/file/d/1dhvh3G1T6t5KwY4_BxO1HjhICyYRR2J_/view",
        team: [
            { name: "Heather McGee", linkedinUrl: "https://www.linkedin.com/in/heather-mcgee-cat/" },
            { name: "Aldhair Vera", linkedinUrl: "https://www.linkedin.com/in/aldhairvera/" },
            { name: "Maria Tam", linkedinUrl: "https://www.linkedin.com/in/mariawktam/" },
            { name: "Vijay Kagupati (Me)", linkedinUrl: "https://www.linkedin.com/in/vijaykagupati/" },
        ],
    },
    {
        id: 7,
        image: mutantGuns,
        title: "Mutant Guns",
        shortDescription: "Explosive & bouncy weapon mods · FOV stealth · Minimap overlay · 1 week",
        blockColor: "cobalt",
        motif: "cursor",
        tags: ["vr"],
        category: "VR Development, Game Development",
        fullDescription: `A VR game
prototype where you can equip your weapons with wild modifiers! Think Explosive bullets that
detonate on impact 💥 or Bouncy rounds that ricochet across walls 🎯.

Here's what you can expect:

Choose Modifier: Explosive, Bouncy, or more surprises in the hopper.

Navigate Stealthy Patrols: Sneak past patrolling troops without being seen.

Minimap & Stats Overlay: Keep tabs on your location, elapsed time ⏱, and enemies left 🔢.`,
        technologies: ["Unity", "C#", "Quest", "XR Interaction Toolkit"],
        duration: "1 Week",
        role: "Solo Developer",
        challenges: [
            "Implementing realistic weapon physics and handling",
            "Creating enemy AI with a FOV system",
            "Balancing action-packed gameplay with VR comfort",
        ],
        features: [
            "Diverse weapon modifiers for unique gameplay",
            "Immersive street environment",
            "Best Time = Best Score",
            "Realistic weapon handling and reloading mechanics",
        ],
        githubLink: "",
        liveDemoLink: "https://youtu.be/lFb2dQAKMBM",
    },
    {
        id: 8,
        image: project3,
        title: "Virtual Aircraft Experience",
        shortDescription: "Interactive cockpit + landing gear + interior/exterior · 3 months at CXR",
        blockColor: "sun",
        motif: "macwin",
        tags: ["vr"],
        category: "VR Development",
        fullDescription: `During my VR Development Internship at CXR, I was responsible for the interactive functionality of a VR Aircraft. Working alongside a 3D Modeler, I utilized Unity and C# to implement complex interactions for various components, including doors, landing gear, engine, and comprehensive interior/exterior details.

This project challenged me to create realistic and intuitive interactions within a virtual environment, ensuring that users could explore and interact with the aircraft as they would in real life. The experience includes detailed cockpit controls, passenger cabin interactions, and exterior inspections.`,
        technologies: ["Unity", "C#", "Blender", "Quest 2", "XR Interaction Toolkit"],
        duration: "3 months",
        role: "VR Developer",
        challenges: [
            "Implementing realistic physics for aircraft components",
            "Optimizing performance for Quest 2 hardware",
            "Creating intuitive user interactions for complex systems",
        ],
        features: [
            "Interactive cockpit controls",
            "Realistic door and landing gear mechanisms",
            "Detailed interior and exterior exploration",
            "Quest 2 hand tracking integration",
        ],
        githubLink: "",
        liveDemoLink: "",
    },
    {
        id: 9,
        image: project1,
        title: "Spectra - Aid for ASD/ID Children",
        shortDescription: "Road crossing + social interactions + life skills · Smart India Hackathon",
        blockColor: "tomato",
        motif: "floppy",
        tags: ["vr"],
        category: "VR Development",
        fullDescription: `Spectra is an innovative Interactive VR tool designed specifically for aiding children with Autism Spectrum Disorder (ASD) and Intellectual Disabilities (ID) in developing crucial life skills and educational competencies.

As part of our submission for Smart India Hackathon 2025, our team conducted extensive research into the specific needs and challenges faced by children with ASD/ID. We developed a comprehensive prototype featuring various virtual experiences, including road crossing simulations, social interaction scenarios, and daily life skill training modules.

The application uses carefully designed virtual environments that provide a safe, controlled space for children to practice and learn essential skills without real-world risks.`,
        technologies: ["Unity", "C#", "Blender", "Quest 2", "XR Interaction Toolkit"],
        duration: "4 months",
        role: "Lead Developer",
        challenges: [
            "Designing age-appropriate and accessible interfaces",
            "Creating calming and non-overwhelming virtual environments",
            "Implementing progress tracking and adaptive learning systems",
        ],
        features: [
            "Road crossing safety training",
            "Social interaction simulations",
            "Daily life skills practice",
            "Progress tracking for educators and parents",
            "Customizable difficulty levels",
        ],
        githubLink: "https://github.com/VijayKagupati/Spectra-VR-Aid",
        liveDemoLink: "",
    },
    {
        id: 10,
        image: traffic,
        title: "Traffic Simulation with WSN",
        shortDescription: "NS-3 + SUMO integration · Adaptive lights + emergency detect · 6 months",
        blockColor: "cobalt",
        motif: "hud",
        tags: ["simulation"],
        category: "Simulation & Networking",
        fullDescription: `An advanced intelligent traffic management system that seamlessly integrates Network Simulator 3 (NS-3) with SUMO (Simulation of Urban Mobility) to create a comprehensive traffic optimization solution.

The system employs wireless sensor networks strategically placed throughout the simulation environment to collect real-time vehicle data, detect emergency situations, and adaptively control traffic light patterns. The integration provides color-coded visualization to help traffic management authorities make informed decisions about traffic flow optimization.

This project demonstrates the practical application of IoT and wireless sensor networks in smart city infrastructure, showcasing how data-driven approaches can significantly improve urban traffic management.`,
        technologies: ["NS-3", "SUMO", "C++", "Python", "Shell", "Ubuntu WSL", "TraCI", "WSN"],
        duration: "6 months",
        role: "Systems Developer",
        challenges: [
            "Integrating two complex simulation platforms",
            "Real-time data processing and analysis",
            "Optimizing sensor network topology for maximum coverage",
        ],
        features: [
            "Real-time traffic monitoring",
            "Adaptive traffic light control",
            "Emergency vehicle detection and prioritization",
            "Color-coded traffic flow visualization",
            "Performance analytics and reporting",
        ],
        githubLink: "https://github.com/VijayKagupati/Traffic-Simulation-with-Wireless-Sensor-Networks",
        liveDemoLink: "",
    },
    {
        id: 11,
        image: project2,
        title: "The Gallery - MobileVR",
        shortDescription: "Cardboard museum · Gaze-based nav · Cross-platform Android · 2 months",
        blockColor: "sun",
        motif: "cursor",
        tags: ["vr"],
        category: "Mobile VR",
        fullDescription: `The Gallery is an immersive Mobile VR project designed for Google Cardboard that transforms your smartphone into a portal to a virtual museum experience. Users can explore a beautifully crafted gallery environment, viewing artwork and exhibits in a fully immersive 3D space.

The application features intuitive gaze-based navigation where users can move through the museum by looking down, creating a natural and comfortable exploration experience. The project showcases the potential of affordable VR solutions in making cultural and educational content accessible to a broader audience.

Built with mobile optimization in mind, The Gallery maintains smooth performance across various Android devices while delivering a rich visual experience that rivals desktop VR applications.`,
        technologies: ["Unity", "C#", "Google Cardboard", "ARCore", "Android"],
        duration: "2 months",
        role: "Solo Developer",
        challenges: [
            "Optimizing performance for mobile hardware",
            "Implementing comfortable gaze-based locomotion",
            "Creating an engaging museum environment with limited resources",
        ],
        features: [
            "Gaze-based movement system",
            "Immersive museum environment",
            "Cross-platform Android compatibility",
            "Optimized for various smartphone sizes",
            "Interactive artwork viewing",
        ],
        githubLink: "https://github.com/VijayKagupati/VR-Art-Gallery",
        liveDemoLink: "",
    },
];

/**
 * Look up a project by string or numeric id.
 * @param {string|number} id
 * @returns {object|undefined}
 */
export function getProjectById(id) {
    return projects.find((p) => p.id === parseInt(id, 10));
}
